import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import UserProfileModal from "./UserProfileModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import {
  MessageCircle,
  Send,
  X,
  User,
  Loader2,
  MoreVertical,
  Trash2,
  Flag,
  Settings,
  Volume2,
  VolumeX
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Chat size options
const CHAT_SIZES = {
  single: { height: "h-[120px]", messagesHeight: "h-[40px]", label: "Single Line" },
  small: { height: "h-[220px]", messagesHeight: "h-[100px]", label: "Small" },
  medium: { height: "h-[350px]", messagesHeight: "h-[230px]", label: "Medium" },
  large: { height: "h-[500px]", messagesHeight: "h-[380px]", label: "Large" },
};

const Shoutbox = () => {
  const { user, updateUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [guildMessages, setGuildMessages] = useState({});
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [chatTab, setChatTab] = useState("global"); // "global" or "guild_<id>"
  const scrollRef = useRef(null);
  const guildScrollRefs = useRef({});
  const inputRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const isSendingRef = useRef(false);

  // Chat settings
  const [chatEnabled, setChatEnabled] = useState(() => {
    const saved = localStorage.getItem("shoutbox_enabled");
    return saved !== null ? saved === "true" : true;
  });
  const [chatSize, setChatSize] = useState(() => {
    return localStorage.getItem("shoutbox_size") || "medium";
  });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem("shoutbox_sound");
    return saved !== null ? saved === "true" : true;
  });

  // New message notification
  const [unreadCount, setUnreadCount] = useState(0);

  // User Profile Modal State
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Report Modal State
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMessage, setReportMessage] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);

  const isAdmin = user?.is_admin || user?.is_junior_admin;

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem("shoutbox_enabled", chatEnabled.toString());
  }, [chatEnabled]);

  useEffect(() => {
    localStorage.setItem("shoutbox_size", chatSize);
  }, [chatSize]);

  useEffect(() => {
    localStorage.setItem("shoutbox_sound", soundEnabled.toString());
  }, [soundEnabled]);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled) {
      try {
        const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleA4AQJ7X0px+Dg0/mtXUnnkODz6W0dGdfhEQPJHOzpuBExI6jMrLmYMVFDiHxsiWhhcXNYLCxZSJGRk0fL/CkosbrDGCtL+PjRyuL4C0v46OHa4vf7K9jY8drTB/sr2NjhyuMH+yvY2OHK0wf7K9jY4crTB/sr2NjhytMH+yvY2OHK0wfrK9jY4crTB+sr2NjhytMH6yvY2OHK0wfrG8jI0bqy98sLuLjBqqLnuvuoqLGakueq65iYoYqC15rbiIiRenLHisuIeJF6Ysd6y3hogWpSx2rLeGiBWlLHast4aIFaUsdqy3hogVpSx2rLeGiBWlLHast4aIFaUsdqy3hYgVpSx2rLeFiBWlLHast4WIFaQrdqy3hYcVpCt1q7aEhxSkK3WrtoSHFKQrdau2hIcUpCt1q7aEhxSkK3WrtoSHFKQrdau2hIcUpCt1q7aEhxSkK3WrtoSHFKQrdau2hIcUpCt1q7aEhxSkK3WrtoSHFKQrdau2hIcUpCt1q7aEhxSkK3WrtoSHFKQrdau2hIcUpCt1q7aEhxOjKnSqtYOGE6MqdKq1g4YToypzqbSDhhOjKnOptIOGE6MqdKq1g4Y=");
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {
        // Ignore audio errors
      }
    }
  }, [soundEnabled]);

  const lastTimestampRef = useRef(null);
  const lastGuildTimestampRefs = useRef({});

  // Get user's guilds (memoized to prevent re-render loops)
  const userGuilds = useMemo(() => user?.guilds || [], [user?.guilds]);

  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const params = { limit: 50, channel: "global" };
      if (!initial && lastTimestampRef.current) {
        params.since = lastTimestampRef.current;
      }
      const response = await axios.get(`${API_URL}/api/shoutbox/messages`, { params });
      const newMessages = response.data.messages || [];
      
      if (newMessages.length > 0) {
        lastTimestampRef.current = newMessages[newMessages.length - 1].created_at;
      }
      
      if (initial) {
        if (newMessages.length > 0) {
          lastTimestampRef.current = newMessages[newMessages.length - 1].created_at;
        }
        lastMessageCountRef.current = newMessages.length;
        setMessages(newMessages);
      } else if (newMessages.length > 0) {
        if (!isOpen || chatTab !== "global") {
          setUnreadCount(prev => prev + newMessages.length);
          playNotificationSound();
        }
        setMessages(prev => [...prev, ...newMessages]);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  }, [isOpen, chatTab, playNotificationSound]);

  const fetchGuildMessages = useCallback(async (guildId, initial = false) => {
    if (!guildId) return;
    const channel = `guild_${guildId}`;
    try {
      const params = { limit: 50, channel };
      const lastTs = lastGuildTimestampRefs.current[guildId];
      if (!initial && lastTs) {
        params.since = lastTs;
      }
      const response = await axios.get(`${API_URL}/api/shoutbox/messages`, { params });
      const newMessages = response.data.messages || [];
      
      if (newMessages.length > 0) {
        lastGuildTimestampRefs.current[guildId] = newMessages[newMessages.length - 1].created_at;
      }
      
      if (initial) {
        if (newMessages.length > 0) {
          lastGuildTimestampRefs.current[guildId] = newMessages[newMessages.length - 1].created_at;
        }
        setGuildMessages(prev => ({ ...prev, [guildId]: newMessages }));
      } else if (newMessages.length > 0) {
        setGuildMessages(prev => ({ ...prev, [guildId]: [...(prev[guildId] || []), ...newMessages] }));
      }
    } catch (error) {
      console.error("Failed to fetch guild messages:", error);
    }
  }, []);

  // Initial load and polling
  useEffect(() => {
    let timeoutId;
    let isActive = true;

    const pollMessages = async () => {
      if (!isActive) return;
      try {
        const promises = [fetchMessages(false)];
        userGuilds.forEach(g => promises.push(fetchGuildMessages(g.id, false)));
        await Promise.all(promises);
      } catch (err) {
        console.error("Polling error:", err);
      } finally {
        if (isActive) {
          timeoutId = setTimeout(pollMessages, 2000);
        }
      }
    };

    if (chatEnabled) {
      setLoading(true);
      const initialPromises = [fetchMessages(true)];
      userGuilds.forEach(g => initialPromises.push(fetchGuildMessages(g.id, true)));
      
      Promise.all(initialPromises).finally(() => {
        setLoading(false);
        if (isActive) {
          timeoutId = setTimeout(pollMessages, 2000);
        }
      });

      return () => {
        isActive = false;
        clearTimeout(timeoutId);
      };
    }
  }, [chatEnabled, fetchMessages, fetchGuildMessages, userGuilds]);

  // Clear unread when opening chat
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    // Scroll active guild tab
    if (chatTab.startsWith("guild_")) {
      const guildId = chatTab.replace("guild_", "");
      const ref = guildScrollRefs.current[guildId];
      if (ref) ref.scrollTop = ref.scrollHeight;
    }
  }, [guildMessages, chatTab]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user || isSendingRef.current || sending) return;

    const channel = chatTab.startsWith("guild_") ? chatTab : "global";

    isSendingRef.current = true;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/shoutbox/messages?channel=${channel}`, {
        message: newMessage.trim()
      });
      setNewMessage("");
      if (chatTab.startsWith("guild_")) {
        const guildId = chatTab.replace("guild_", "");
        fetchGuildMessages(guildId);
      } else {
        fetchMessages();
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Please wait a few seconds before sending another message");
      } else {
        toast.error(error.response?.data?.detail || "Failed to send message");
      }
    } finally {
      isSendingRef.current = false;
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API_URL}/api/shoutbox/messages/${messageId}`);
      toast.success("Message deleted");
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete message");
    }
  };

  const handleViewProfile = (msg) => {
    setSelectedUserId(msg.user_id);
    setProfileModalOpen(true);
  };

  const handleReportMessage = (msg) => {
    setReportMessage(msg);
    setReportReason("");
    setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!reportReason.trim() || !reportMessage) return;

    setSubmittingReport(true);
    try {
      await axios.post(`${API_URL}/api/shoutbox/report`, {
        message_id: reportMessage.id,
        reason: reportReason.trim()
      });
      toast.success("Report submitted successfully");
      setShowReportModal(false);
      setReportMessage(null);
      setReportReason("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmittingReport(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const currentSize = CHAT_SIZES[chatSize] || CHAT_SIZES.medium;

  // Don't render anything if not logged in OR chat is disabled
  if (!user || !chatEnabled) return null;

  // Show button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 rounded-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-lg flex items-center justify-center transition-all hover:scale-110 hover:shadow-xl"
        title="Open Global Chat"
      >
        <MessageCircle className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold px-1 animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      {/* Chat Window - Positioned to the right */}
      <div className="fixed bottom-4 right-4 z-50 w-[380px] max-w-[90vw]">
        <div className={`bg-card/95 backdrop-blur-lg border border-border rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${currentSize.height}`}>
          {/* Chat Header */}
          <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-primary/20 to-primary/5 border-b border-border">
            <div className="flex items-center gap-1 overflow-x-auto">
              <MessageCircle className="w-4 h-4 text-primary flex-shrink-0" />
              <button
                onClick={() => setChatTab("global")}
                className={`px-2 py-0.5 text-xs font-semibold rounded transition-all whitespace-nowrap ${chatTab === "global" ? "bg-primary/30 text-primary" : "text-muted-foreground hover:text-foreground"}`}
              >
                Global
              </button>
              {userGuilds.map(g => (
                <button
                  key={g.id}
                  onClick={() => setChatTab(`guild_${g.id}`)}
                  className={`px-2 py-0.5 text-xs font-semibold rounded transition-all whitespace-nowrap ${chatTab === `guild_${g.id}` ? "bg-amber-500/30 text-amber-300" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {g.name}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {/* Sound Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setSoundEnabled(!soundEnabled)}
                title={soundEnabled ? "Mute notifications" : "Enable notifications"}
              >
                {soundEnabled ? (
                  <Volume2 className="w-3.5 h-3.5" />
                ) : (
                  <VolumeX className="w-3.5 h-3.5 text-muted-foreground" />
                )}
              </Button>
              {/* Settings Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Settings className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Chat Size</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {Object.entries(CHAT_SIZES).map(([key, value]) => (
                    <DropdownMenuItem
                      key={key}
                      onClick={() => setChatSize(key)}
                      className={chatSize === key ? "bg-primary/20" : ""}
                    >
                      {value.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setChatEnabled(false);
                      setIsOpen(false);
                    }}
                    className="text-destructive"
                  >
                    Disable Chat
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsOpen(false)}
                title="Close chat"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div 
            ref={(el) => {
              if (chatTab === "global") scrollRef.current = el;
              else if (chatTab.startsWith("guild_")) guildScrollRefs.current[chatTab.replace("guild_", "")] = el;
            }}
            className={`overflow-y-auto p-2 space-y-2 ${currentSize.messagesHeight}`}
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (() => {
              const activeMessages = chatTab === "global" 
                ? messages 
                : (guildMessages[chatTab.replace("guild_", "")] || []);
              return activeMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm">
                  <MessageCircle className="w-8 h-8 mb-1 opacity-50" />
                  <p>No messages yet</p>
                </div>
              ) : (
                activeMessages.slice(chatSize === "single" ? -3 : 0).map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-1.5 group ${
                    msg.user_id === user?.id ? "flex-row-reverse" : ""
                  }`}
                >
                  {/* Avatar */}
                  <button
                    onClick={() => handleViewProfile(msg)}
                    className="flex-shrink-0 hover:opacity-80 transition-opacity"
                  >
                    {msg.avatar ? (
                      <img
                        src={msg.avatar}
                        alt={msg.username}
                        className="w-6 h-6 rounded-full object-cover border border-border"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center border border-border">
                        <User className="w-3 h-3 text-primary" />
                      </div>
                    )}
                  </button>

                  {/* Message */}
                  <div
                    className={`max-w-[75%] ${
                      msg.user_id === user?.id
                        ? "bg-primary text-primary-foreground rounded-xl rounded-br-sm"
                        : "bg-muted rounded-xl rounded-bl-sm"
                    } px-2 py-1`}
                  >
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleViewProfile(msg)}
                        className={`text-[10px] font-semibold hover:underline ${
                          msg.user_id === user?.id ? "text-primary-foreground/80" : "text-foreground"
                        }`}
                      >
                        {msg.username}
                      </button>
                      <span className={`text-[10px] ${
                        msg.user_id === user?.id ? "text-primary-foreground/60" : "text-muted-foreground"
                      }`}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                    <p className="text-xs break-words">{msg.message}</p>
                  </div>

                  {/* Actions */}
                  <div className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                    msg.user_id === user?.id ? "order-first" : ""
                  }`}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-5 w-5">
                          <MoreVertical className="w-2.5 h-2.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align={msg.user_id === user?.id ? "start" : "end"}>
                        <DropdownMenuItem onClick={() => handleViewProfile(msg)}>
                          <User className="w-3 h-3 mr-2" />
                          View Profile
                        </DropdownMenuItem>
                        {msg.user_id !== user?.id && (
                          <DropdownMenuItem onClick={() => handleReportMessage(msg)}>
                            <Flag className="w-3 h-3 mr-2 text-amber-500" />
                            Report
                          </DropdownMenuItem>
                        )}
                        {(msg.user_id === user?.id || isAdmin) && (
                          <DropdownMenuItem 
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-3 h-3 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))
              );
            })()}
          </div>

          {/* Input Area */}
          <form 
            onSubmit={handleSendMessage}
            className="p-2 border-t border-border bg-background/50"
          >
            <div className="flex gap-1.5">
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                maxLength={500}
                disabled={sending}
                className="flex-1 h-8 text-sm bg-background"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!newMessage.trim() || sending}
                className="h-8 w-8 bg-primary hover:bg-primary/90"
              >
                {sending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* User Profile Modal */}
      <UserProfileModal
        open={profileModalOpen}
        onOpenChange={setProfileModalOpen}
        userId={selectedUserId}
      />

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-amber-500" />
              Report Message
            </DialogTitle>
            <DialogDescription>
              Report inappropriate content.
            </DialogDescription>
          </DialogHeader>

          {reportMessage && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50 border border-border/50">
                <div className="flex items-center gap-2 mb-2">
                  {reportMessage.avatar ? (
                    <img src={reportMessage.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-3 h-3 text-primary" />
                    </div>
                  )}
                  <span className="text-sm font-medium">{reportMessage.username}</span>
                </div>
                <p className="text-sm">{reportMessage.message}</p>
              </div>

              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  placeholder="Why are you reporting this?"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  maxLength={500}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportModal(false)}>Cancel</Button>
            <Button 
              onClick={submitReport}
              disabled={!reportReason.trim() || submittingReport}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {submittingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Shoutbox;
